import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Headline,
  IconButton,
  Layout,
  LayoutProps,
  Text,
  TextButton,
} from '@oneaudi/unified-web-components';
import {
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_500,
  SYS_SPACE_RELATIVE_LG,
  ThemeProvider,
} from '@oneaudi/unified-web-common';
import { ACTIONS, SELECTORS } from '@oneaudi/stck-store';
import { useI18n, useI18nBulk } from '@oneaudi/i18n-context';
import {
  dynamicStylingHelper,
  getRelatedProduct,
  MYAUDI_WISHLIST_LOCAL_STORAGE_KEY,
  MyAudiWishlistLoginLayer,
  useAuthContext,
  useMyAudiWishlistContext,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { getAppContext, type PlpConfiguration } from '../../hooks/tileHook';
import {
  trackAddToFav,
  trackDetailsPageNav,
  trackRemoveFromFav,
} from '../../tracking/tilesTracking';
import {
  ButtonContainer,
  FavoritesContainer,
  FavoritesOverlay,
  GridButtonContainer,
} from './ActionButtons.styles';
import { useVehicleDetailsPageUrl } from '../../hooks/useVehicleDetailsPageUrl';
import { SYS_SPACE_RELATIVE_MD, SYS_SPACE_RELATIVE_XL } from '@oneaudi/unified-web-alpha-common';
import styled from 'styled-components';
import type { ContentService } from '@oneaudi/content-service';
import { FocusLayerSizeV2, type LayerManagerV27 } from '@volkswagen-onehub/layer-manager';

interface Props {
  vehicle: VehicleBasic;
}

const ActionButtons: React.FC<Props> = ({ vehicle }) => {
  const detailsPageLink = useVehicleDetailsPageUrl(vehicle) || '';
  const goToDetailsLabel = useI18n({
    id: 'nemo.ui.sc.result.details.button',
    defaultMessage: 'Go to Details',
  });
  const addFavoriteLabel = useI18n({ id: 'nemo.ui.sc.result.addfavorite', defaultMessage: '' });
  const favoriteAreaLabelNotSaved = useI18n({
    id: 'stockcars.button.favourites.is-not-saved.arialabel',
    defaultMessage: '',
  });
  const favoriteAreaLabelSaved = useI18n({
    id: 'stockcars.button.favourites.is-saved.arialabel',
    defaultMessage: '',
  });
  const removeFavoriteLabel = useI18n({
    id: 'nemo.ui.sc.result.removefavorite',
    defaultMessage: '',
  });
  const favoritesLayerHeadline = useI18n({
    id: 'nemo.ui.sc.favorites.layer.headline',
    defaultMessage: '',
  });
  const favoritesLayerCopy = useI18n({ id: 'nemo.ui.sc.favorites.layer.copy', defaultMessage: '' });
  const favoritesLayerYes = useI18n({
    id: 'nemo.ui.sc.favorites.layer.button.yes',
    defaultMessage: 'Yes',
  });
  const favoritesLayerNo = useI18n({
    id: 'nemo.ui.sc.favorites.layer.button.no',
    defaultMessage: 'No',
  });
  const i18nMyAudiWishlistLoginLayer = useI18nBulk({
    headline: { id: 'vtp-myaudi-wishlist.login-layer.headline' },
    copy1: { id: 'vtp-myaudi-wishlist.login-layer.copy.1' },
    copy2: { id: 'vtp-myaudi-wishlist.login-layer.copy.2' },
    copy3: { id: 'vtp-myaudi-wishlist.login-layer.copy.3' },
    loginButton: { id: 'vtp-myaudi-wishlist.login-layer.login-button' },
  });

  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;

  const { authService, isAuthenticated } = useAuthContext();

  const appContext = getAppContext();
  const { vtpConfiguration } = audiContentService?.getContent()?.fields as PlpConfiguration;
  const { enableMyAudiWishlist } = vtpConfiguration?.fields ?? {};

  const dispatch = useDispatch();
  const isFavorite = useSelector((state: {}) =>
    SELECTORS.VEHICLES.isFavoriteVehicle(state, vehicle.id)
  );
  const [showFavoritesLayer, setShowFavoritesLayer] = useState(false);

  const myAudiWishlistApi = useMyAudiWishlistContext();

  const isInMyAudiWishlist = enableMyAudiWishlist && myAudiWishlistApi.isInWishlist(vehicle.id);

  const isNewCar = vehicle.type.toLowerCase() === 'n';

  // TODO should we move this somewhere up the tree so it doesnt need to run in all the ActionButtons?
  // But its better to have it together with the rest of the wishlist logic
  useEffect(() => {
    if (!enableMyAudiWishlist || !isAuthenticated) return;

    const myAudiVehicleIdToWishlist = localStorage.getItem(MYAUDI_WISHLIST_LOCAL_STORAGE_KEY);

    if (!myAudiVehicleIdToWishlist || myAudiVehicleIdToWishlist !== vehicle.id) return;

    localStorage.removeItem(MYAUDI_WISHLIST_LOCAL_STORAGE_KEY);

    if (isInMyAudiWishlist) return;

    myAudiWishlistApi.add(vehicle.id, isNewCar ? 'NEW' : 'USED');
  }, [enableMyAudiWishlist, isAuthenticated, isInMyAudiWishlist, vehicle.id]);

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;

  const layerManager = useServicesContext().getAdditionalService(
    'gfa:layer-manager'
  ) as LayerManagerV27;

  const relatedProduct = getRelatedProduct(vehicle);

  const [layerNode, setLayerNode] = useState<HTMLDivElement | null>(null);

  const addFavorites = () => {
    if (enableMyAudiWishlist) {
      myAudiWishlistApi.add(vehicle.id, isNewCar ? 'NEW' : 'USED');
    } else {
      dispatch(
        ACTIONS.FAVORITE_VEHICLES.addFavoriteVehicleId({
          vehicleId: vehicle.id,
        })
      );
    }

    trackAddToFav(trackingService, addFavoriteLabel, relatedProduct);
  };

  const removeFavorites = () => {
    if (enableMyAudiWishlist) {
      myAudiWishlistApi.remove(vehicle.id);
    } else {
      dispatch(
        ACTIONS.FAVORITE_VEHICLES.removeFavoriteVehicleId({
          vehicleId: vehicle.id,
        })
      );
    }

    setShowFavoritesLayer(false);
    trackRemoveFromFav(trackingService, removeFavoriteLabel, relatedProduct);
  };

  const handleFavoritesClick = () => {
    if (enableMyAudiWishlist) {
      if (!isAuthenticated) {
        layerManager.openFocusLayer(
          () => <div ref={setLayerNode} />,
          {},
          {
            size: FocusLayerSizeV2.A,
            userCloseable: true,
          }
        );

        return;
      }

      if (isInMyAudiWishlist) {
        if (appContext !== 'results') {
          setShowFavoritesLayer(true);
        } else {
          removeFavorites();
        }
      } else {
        addFavorites();
      }

      return;
    }

    if (isFavorite) {
      if (appContext !== 'results') {
        setShowFavoritesLayer(true);
      } else {
        removeFavorites();
      }
    } else {
      addFavorites();
    }
  };

  return (
    <>
      <ButtonContainer>
        <TextButton
          size={dynamicStylingHelper(undefined, 'sm')}
          variant="primary"
          data-testid="detailsBtn"
          href={detailsPageLink}
          isFullWidth
          onClick={() =>
            trackDetailsPageNav(
              trackingService,
              `${vehicle.symbolicCarline?.description}`,
              detailsPageLink,
              'button'
            )
          }
        >
          {goToDetailsLabel}
        </TextButton>
        <FavoritesContainer>
          <IconButton
            variant="secondary"
            icon={isFavorite || isInMyAudiWishlist ? 'favorite-active' : 'favorite'}
            size={dynamicStylingHelper('md', 'sm')}
            type="button"
            onClick={handleFavoritesClick}
            isLoading={myAudiWishlistApi.isLoading}
            data-testid="favoritesBtn"
            aria-label={
              isFavorite || isInMyAudiWishlist ? favoriteAreaLabelSaved : favoriteAreaLabelNotSaved
            }
          />
        </FavoritesContainer>
      </ButtonContainer>
      {showFavoritesLayer && (
        <ThemeProvider theme={{ name: 'dark' }}>
          <FavoritesOverlay>
            <StyledLayout
              direction="column"
              justify="center"
              margin={[0, 'var(--spacing-relative-xl)']}
            >
              <Headline
                variant={{
                  order: dynamicStylingHelper('2', '3'),
                  weight: 'normal',
                  type: 'headline',
                }}
                tag="h2"
                margin={[0, 0, dynamicStylingHelper(SYS_SPACE_FIXED_150, SYS_SPACE_RELATIVE_MD), 0]}
              >
                {favoritesLayerHeadline}
              </Headline>
              <Text
                variant={{ order: '2', style: 'normal', type: 'copy' }}
                margin={[0, 0, dynamicStylingHelper(SYS_SPACE_FIXED_500, SYS_SPACE_RELATIVE_XL), 0]}
              >
                {favoritesLayerCopy}
              </Text>
              <GridButtonContainer data-testid="removeFavoriteLayer">
                <TextButton
                  variant="primary"
                  onClick={removeFavorites}
                  isLoading={myAudiWishlistApi.isLoading}
                  data-testid="removeFavorite"
                  size={dynamicStylingHelper('md', 'sm')}
                >
                  {favoritesLayerYes}
                </TextButton>
                <TextButton
                  variant="secondary"
                  onClick={() => setShowFavoritesLayer(false)}
                  data-testid="closeRemoveFavoriteLayer"
                  size={dynamicStylingHelper('md', 'sm')}
                >
                  {favoritesLayerNo}
                </TextButton>
              </GridButtonContainer>
            </StyledLayout>
          </FavoritesOverlay>
        </ThemeProvider>
      )}

      {layerNode &&
        ReactDOM.createPortal(
          <MyAudiWishlistLoginLayer
            authService={authService}
            vehicle={vehicle}
            translations={i18nMyAudiWishlistLoginLayer}
          />,
          layerNode
        )}
    </>
  );
};

export default ActionButtons;

export const StyledLayout = styled((props) => <Layout {...props} />)<LayoutProps>`
  width: ${dynamicStylingHelper('unset', `calc(100% - 2*${SYS_SPACE_RELATIVE_LG})`)};
`;
